import type { ListCustomersResponse, CustomerSummaryDto, OwnerEntity } from '@bhops/shared';
import { apiClient } from './client';

export interface ListCustomersQuery {
  segment?: string;
  salesOwnerCode?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  includeInactive?: boolean;
  // round7 §1-1 — ADMIN 퍼지 진입점.
  includeDeleted?: boolean;
}

// Plan v0.3.0 §G-2 — server-side DTO marks the 4 "core" fields
// optional and fills defaults when missing (legalName/displayName
// → '', segment → UNKNOWN (plan-v0.4.0.md §O-3), ownerEntity →
// caller's own). Mirror that on the wire-format type so the
// windows client can omit fields the operator skipped.
export interface CreateCustomerBody {
  legalName?: string;
  displayName?: string;
  segment?: string;
  ownerEntity?: OwnerEntity;
  salesOwnerCode?: string;
  countryCode?: string;
  notes?: string;
}

export const customersApi = {
  async list(query: ListCustomersQuery = {}): Promise<ListCustomersResponse> {
    const { data } = await apiClient.get<ListCustomersResponse>('/customers', { params: query });
    return data;
  },
  async get(id: string): Promise<CustomerSummaryDto> {
    const { data } = await apiClient.get<CustomerSummaryDto>(`/customers/${id}`);
    return data;
  },
  async create(body: CreateCustomerBody): Promise<CustomerSummaryDto> {
    const { data } = await apiClient.post<CustomerSummaryDto>('/customers', body);
    return data;
  },
  async update(id: string, body: UpdateCustomerBody): Promise<CustomerSummaryDto> {
    const { data } = await apiClient.patch<CustomerSummaryDto>(`/customers/${id}`, body);
    return data;
  },
  // docs/work_log/p3-cleanup — backend customers.controller.ts:51 의
  // `softDelete` (DELETE /customers/:id, ADMIN 한정) 와 1:1 매핑.
  // CustomerDetailPage.tsx:91 의 deleteMx mutation 이 이 메서드를 호출.
  // 서버는 soft-delete (deletedAt 채움) 하므로 클라이언트는 단순 fire-
  // and-forget; onSuccess 에서 invalidate + 마스터 페이지로 redirect.
  async remove(id: string): Promise<void> {
    await apiClient.delete(`/customers/${id}`);
  },
  // data-lifecycle plan §3 — soft-delete 복구(셀프).
  async restore(id: string): Promise<void> {
    await apiClient.post(`/customers/${encodeURIComponent(id)}/restore`);
  },
  // round7 R2 — 삭제 확인 모달용 의존성 요약.
  // round7 §1-1 — 퍼지(연쇄 완전 삭제, ADMIN). 서버가 이름 일치 재검증.
  async purge(id: string, confirmName: string): Promise<{ purged: Record<string, number> }> {
    const { data } = await apiClient.post<{ purged: Record<string, number> }>(
      `/admin/customers/${id}/purge`,
      { confirmName },
    );
    return data;
  },
  async dependencySummary(id: string): Promise<{
    activeContracts: number;
    deployedRobots: number;
    reservedRobots: number;
    openServiceOrders: number;
  }> {
    const { data } = await apiClient.get<{
      activeContracts: number;
      deployedRobots: number;
      reservedRobots: number;
      openServiceOrders: number;
    }>(`/customers/${id}/dependency-summary`);
    return data;
  },
};

export interface UpdateCustomerBody {
  legalName?: string;
  displayName?: string;
  segment?: string;
  salesOwnerCode?: string;
  countryCode?: string;
  active?: boolean;
  notes?: string | null;
}
